import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable, of } from 'rxjs';
import { catchError, tap, map, switchMap } from 'rxjs/operators';
import { TypographyRepository } from './typography-repository.service';
import { TypographyStore } from './typography-store.service';
import { TypographyEventBus } from './typography-event-bus.service';
import {
  FontFamily,
  TypographyScale,
  TypographyConfig,
  TextStyle,
  TypographyFormData,
  TypographyConfigCreateRequest,
  TypographyState
} from './typography.interface';

/** Convert design-token pixel values to rem for CSS (relative to default 16px root). */
const ROOT_FONT_PX_REFERENCE = 16;

@Injectable({
  providedIn: 'root'
})
export class TypographyManagementService {
  private renderer: Renderer2;

  constructor(
    private readonly rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly typographyRepository: TypographyRepository,
    private readonly typographyStore: TypographyStore,
    private readonly eventBus: TypographyEventBus
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.setupEventListeners();
  }

  /**
   * Setup event listeners for typography updates
   */
  private setupEventListeners(): void {
    console.log('🎨 TypographyManagementService: Setting up event listeners...');

    // Listen for typography update events
    this.eventBus.onTypographyUpdate().subscribe(event => {
      console.log('🎨 TypographyManagementService: Received typography update event:', event);
      this.applyTypographyConfig(event);
    });

    // Listen for typography error events
    this.eventBus.onTypographyError().subscribe(error => {
      console.error('🎨 TypographyManagementService: Received typography error:', error);
      this.applyFallbackTypography();
    });
  }

  /**
   * Fetch complete typography data from backend
   */
  getTypographyData(): Observable<TypographyState> {
    console.log('🚀 TypographyManagementService: Fetching typography data...');

    this.typographyStore.setLoading(true);

    return this.typographyRepository.getFontFamilies().pipe(
      switchMap(fontFamilies => {
        this.typographyStore.updateFontFamilies(fontFamilies);

        return this.typographyRepository.getTypographyScales().pipe(
          switchMap(typographyScales => {
            this.typographyStore.updateTypographyScales(typographyScales);

            return this.typographyRepository.getActiveTypographyConfig().pipe(
              map(activeConfig => {
                this.typographyStore.updateActiveTypographyConfig(activeConfig);

                const state: TypographyState = {
                  fontFamilies,
                  typographyScales,
                  activeTypographyConfig: activeConfig,
                  isLoading: false,
                  lastUpdated: new Date(),
                  error: null
                };

                console.log('✅ TypographyManagementService: Typography data loaded:', state);

                // Apply typography configuration if available, otherwise use fallback
                if (activeConfig) {
                  this.applyTypographyConfig(activeConfig);
                } else {
                  console.warn('⚠️ TypographyManagementService: No active config found, applying fallback typography');
                  this.applyFallbackTypography();
                }

                // Emit loaded event
                this.eventBus.emitTypographyLoaded(state);

                return state;
              })
            );
          })
        );
      }),
      catchError(error => {
        console.error('❌ TypographyManagementService: Error fetching typography data:', error);
        this.typographyStore.setError('Failed to load typography data');
        this.eventBus.emitTypographyError('Failed to load typography data');
        // Apply fallback typography when error occurs
        this.applyFallbackTypography();
        return of(this.typographyStore.getCurrentState());
      }),
      tap(() => {
        this.typographyStore.setLoading(false);
      })
    );
  }

  /**
   * Save typography configuration
   */
  saveTypographyConfig(formData: TypographyFormData): Observable<TypographyConfig> {
    console.log('💾 TypographyManagementService: Saving typography configuration...', formData);

    const configData: TypographyConfigCreateRequest = {
      theme: 1, // Assuming theme ID 1 for now - should be dynamic
      font_family: formData.font_family_id,
      typography_scale: formData.typography_scale_id,
      base_font_size: formData.base_font_size,
      min_font_size: formData.min_font_size,
      max_font_size: formData.max_font_size,
      base_line_height: formData.base_line_height,
      base_letter_spacing: formData.base_letter_spacing,
      font_weight_normal: formData.font_weight_normal,
      font_weight_medium: formData.font_weight_medium,
      font_weight_semibold: formData.font_weight_semibold,
      font_weight_bold: formData.font_weight_bold
    };

    const currentConfig = this.typographyStore.getCurrentActiveTypographyConfig();

    if (currentConfig) {
      // Update existing configuration
      return this.typographyRepository.updateTypographyConfig(currentConfig.id, configData).pipe(
        tap(updatedConfig => {
          console.log('✅ TypographyManagementService: Typography config updated:', updatedConfig);
          this.typographyStore.updateActiveTypographyConfig(updatedConfig);
          this.applyTypographyConfig(updatedConfig);
          this.eventBus.emitTypographySaved(updatedConfig);
        })
      );
    } else {
      // Create new configuration
      return this.typographyRepository.createTypographyConfig(configData).pipe(
        tap(newConfig => {
          console.log('✅ TypographyManagementService: Typography config created:', newConfig);
          this.typographyStore.updateActiveTypographyConfig(newConfig);
          this.applyTypographyConfig(newConfig);
          this.eventBus.emitTypographySaved(newConfig);
        })
      );
    }
  }

  /**
   * Apply typography configuration to CSS variables
   */
  applyTypographyConfig(config: TypographyConfig | null): void {
    console.log('🎨 TypographyManagementService: Applying typography config to CSS variables:', config);

    if (!config) {
      console.warn('⚠️ TypographyManagementService: No typography config provided, applying fallback typography');
      this.applyFallbackTypography();
      return;
    }

    const rootElement = this.document.documentElement;

    // Apply font family - check if exists in config, else use fallback
    // Use both setProperty and renderer.setStyle to ensure it overrides SCSS default (like color service)
    if (config.font_family) {
      const fontFamily = this.buildFontFamilyString(config.font_family);
      // Force set CSS variable using direct DOM manipulation to ensure it overrides SCSS default
      rootElement.style.setProperty('--theme-font-family', fontFamily);
      // Also use renderer as backup
      this.renderer.setStyle(rootElement, '--theme-font-family', fontFamily);
      console.log('🎨 TypographyManagementService: Applied font family from config:', fontFamily);
    } else {
      // Use fallback variable if font_family is not available
      const fallbackFont = '"Poppins", sans-serif';
      rootElement.style.setProperty('--theme-font-family', fallbackFont);
      this.renderer.setStyle(rootElement, '--theme-font-family', fallbackFont);
      console.log('🎨 TypographyManagementService: No font_family in config, using fallback: "Poppins", sans-serif');
    }

    // Apply base font size - use both methods to ensure it overrides SCSS default
    const baseFontSize = this.pxToRem(config.base_font_size);
    rootElement.style.setProperty('--theme-base-font-size', baseFontSize);
    this.renderer.setStyle(rootElement, '--theme-base-font-size', baseFontSize);
    console.log('🎨 TypographyManagementService: Applied base font size:', baseFontSize);

    // Apply line height - use both methods to ensure it overrides SCSS default
    const baseLineHeight = config.base_line_height.toString();
    rootElement.style.setProperty('--theme-base-line-height', baseLineHeight);
    this.renderer.setStyle(rootElement, '--theme-base-line-height', baseLineHeight);
    console.log('🎨 TypographyManagementService: Applied line height:', baseLineHeight);

    // Apply letter spacing - use both methods to ensure it overrides SCSS default
    const baseLetterSpacing = `${config.base_letter_spacing}em`;
    rootElement.style.setProperty('--theme-base-letter-spacing', baseLetterSpacing);
    this.renderer.setStyle(rootElement, '--theme-base-letter-spacing', baseLetterSpacing);
    console.log('🎨 TypographyManagementService: Applied letter spacing:', baseLetterSpacing);

    // Apply font weights - use both methods to ensure they override SCSS default
    rootElement.style.setProperty('--theme-font-weight-normal', config.font_weight_normal.toString());
    this.renderer.setStyle(rootElement, '--theme-font-weight-normal', config.font_weight_normal.toString());

    rootElement.style.setProperty('--theme-font-weight-medium', config.font_weight_medium.toString());
    this.renderer.setStyle(rootElement, '--theme-font-weight-medium', config.font_weight_medium.toString());

    rootElement.style.setProperty('--theme-font-weight-semibold', config.font_weight_semibold.toString());
    this.renderer.setStyle(rootElement, '--theme-font-weight-semibold', config.font_weight_semibold.toString());

    rootElement.style.setProperty('--theme-font-weight-bold', config.font_weight_bold.toString());
    this.renderer.setStyle(rootElement, '--theme-font-weight-bold', config.font_weight_bold.toString());

    console.log('🎨 TypographyManagementService: Applied font weights:', {
      normal: config.font_weight_normal,
      medium: config.font_weight_medium,
      semibold: config.font_weight_semibold,
      bold: config.font_weight_bold
    });

    // Apply text styles if available; otherwise, derive sizes from base size
    if (config.text_styles && config.text_styles.length > 0) {
      this.applyTextStyles(config.text_styles);
    } else {
      // Derive common sizes from base font size as a fallback
      const sizes = this.calculateFontSizes(config.base_font_size);
      rootElement.style.setProperty('--theme-h1-font-size', this.pxToRem(sizes['h1']));
      this.renderer.setStyle(rootElement, '--theme-h1-font-size', this.pxToRem(sizes['h1']));
      rootElement.style.setProperty('--theme-h2-font-size', this.pxToRem(sizes['h2']));
      this.renderer.setStyle(rootElement, '--theme-h2-font-size', this.pxToRem(sizes['h2']));
      rootElement.style.setProperty('--theme-h3-font-size', this.pxToRem(sizes['h3']));
      this.renderer.setStyle(rootElement, '--theme-h3-font-size', this.pxToRem(sizes['h3']));
      rootElement.style.setProperty('--theme-h4-font-size', this.pxToRem(sizes['h4']));
      this.renderer.setStyle(rootElement, '--theme-h4-font-size', this.pxToRem(sizes['h4']));
      rootElement.style.setProperty('--theme-h5-font-size', this.pxToRem(sizes['h5']));
      this.renderer.setStyle(rootElement, '--theme-h5-font-size', this.pxToRem(sizes['h5']));
      rootElement.style.setProperty('--theme-h6-font-size', this.pxToRem(sizes['h6']));
      this.renderer.setStyle(rootElement, '--theme-h6-font-size', this.pxToRem(sizes['h6']));
      rootElement.style.setProperty('--theme-body-font-size', this.pxToRem(sizes['body']));
      this.renderer.setStyle(rootElement, '--theme-body-font-size', this.pxToRem(sizes['body']));
      rootElement.style.setProperty('--theme-body-small-font-size', this.pxToRem(sizes['bodySmall']));
      this.renderer.setStyle(rootElement, '--theme-body-small-font-size', this.pxToRem(sizes['bodySmall']));
      rootElement.style.setProperty('--theme-caption-font-size', this.pxToRem(sizes['caption']));
      this.renderer.setStyle(rootElement, '--theme-caption-font-size', this.pxToRem(sizes['caption']));
      rootElement.style.setProperty('--theme-small-font-size', this.pxToRem(sizes['small']));
      this.renderer.setStyle(rootElement, '--theme-small-font-size', this.pxToRem(sizes['small']));
      console.log('🎨 TypographyManagementService: Applied derived font sizes from base:', sizes);
    }

    console.log('✅ TypographyManagementService: Typography configuration applied successfully');
  }

  /**
   * Apply text styles to CSS variables
   */
  private applyTextStyles(textStyles: TextStyle[]): void {
    console.log('🎨 TypographyManagementService: Applying text styles:', textStyles);

    const rootElement = this.document.documentElement;

    textStyles.forEach(style => {
      const cssVarName = `--theme-${style.style_name.toLowerCase().replace(/\s+/g, '-')}`;

      // Apply font size
      this.renderer.setStyle(rootElement, `${cssVarName}-font-size`, this.pxToRem(style.font_size));

      // Apply font weight
      this.renderer.setStyle(rootElement, `${cssVarName}-font-weight`, style.font_weight);

      // Apply line height if specified
      if (style.line_height) {
        this.renderer.setStyle(rootElement, `${cssVarName}-line-height`, style.line_height.toString());
      }

      // Apply letter spacing if specified
      if (style.letter_spacing) {
        this.renderer.setStyle(rootElement, `${cssVarName}-letter-spacing`, `${style.letter_spacing}em`);
      }

      // Apply text transform
      this.renderer.setStyle(rootElement, `${cssVarName}-text-transform`, style.text_transform);

      console.log(`🎨 TypographyManagementService: Applied text style ${style.style_name}:`, {
        fontSize: this.pxToRem(style.font_size),
        fontWeight: style.font_weight,
        lineHeight: style.line_height,
        letterSpacing: style.letter_spacing,
        textTransform: style.text_transform
      });
    });

    // Map known text styles to common CSS variables used across the app
    const mapToCommonVar = (styleName: string, sizePx: number) => {
      const name = styleName.trim().toLowerCase().replace(/\s+/g, '');
      const setVar = (varName: string, value: string) => {
        rootElement.style.setProperty(varName, value);
        this.renderer.setStyle(rootElement, varName, value);
      };
      if (name === 'h1') setVar('--theme-h1-font-size', this.pxToRem(sizePx));
      if (name === 'h2') setVar('--theme-h2-font-size', this.pxToRem(sizePx));
      if (name === 'h3') setVar('--theme-h3-font-size', this.pxToRem(sizePx));
      if (name === 'h4') setVar('--theme-h4-font-size', this.pxToRem(sizePx));
      if (name === 'h5') setVar('--theme-h5-font-size', this.pxToRem(sizePx));
      if (name === 'h6') setVar('--theme-h6-font-size', this.pxToRem(sizePx));
      if (name === 'body') setVar('--theme-body-font-size', this.pxToRem(sizePx));
      if (name === 'bodysmall' || name === 'body-small' || name === 'smallbody') {
        setVar('--theme-body-small-font-size', this.pxToRem(sizePx));
      }
      if (name === 'caption') setVar('--theme-caption-font-size', this.pxToRem(sizePx));
      if (name === 'small') setVar('--theme-small-font-size', this.pxToRem(sizePx));
    };

    textStyles.forEach(style => {
      if (typeof style.font_size === 'number') {
        mapToCommonVar(style.style_name, style.font_size);
      }
    });
  }

  /**
   * Build font family string from FontFamily object
   */
  private buildFontFamilyString(fontFamily: FontFamily): string {
    let fontFamilyString = `"${fontFamily.name}"`;

    if (fontFamily.fallback_fonts) {
      fontFamilyString += `, ${fontFamily.fallback_fonts}`;
    }

    return fontFamilyString;
  }

  /**
   * Apply fallback typography when error occurs or no data from backend
   */
  private applyFallbackTypography(): void {
    console.log('🎨 TypographyManagementService: Applying fallback typography...');

    const rootElement = this.document.documentElement;

    // Apply fallback values - using SCSS variable default: "Poppins", sans-serif
    // Use both setProperty and renderer.setStyle to ensure it overrides SCSS default
    const fallbackFont = '"Poppins", sans-serif';
    rootElement.style.setProperty('--theme-font-family', fallbackFont);
    this.renderer.setStyle(rootElement, '--theme-font-family', fallbackFont);

    rootElement.style.setProperty('--theme-base-font-size', '1rem');
    this.renderer.setStyle(rootElement, '--theme-base-font-size', '1rem');

    rootElement.style.setProperty('--theme-base-line-height', 'normal');
    this.renderer.setStyle(rootElement, '--theme-base-line-height', 'normal');

    rootElement.style.setProperty('--theme-base-letter-spacing', '0em');
    this.renderer.setStyle(rootElement, '--theme-base-letter-spacing', '0em');

    rootElement.style.setProperty('--theme-font-weight-normal', '400');
    this.renderer.setStyle(rootElement, '--theme-font-weight-normal', '400');

    rootElement.style.setProperty('--theme-font-weight-medium', '500');
    this.renderer.setStyle(rootElement, '--theme-font-weight-medium', '500');

    rootElement.style.setProperty('--theme-font-weight-semibold', '600');
    this.renderer.setStyle(rootElement, '--theme-font-weight-semibold', '600');

    rootElement.style.setProperty('--theme-font-weight-bold', '700');
    this.renderer.setStyle(rootElement, '--theme-font-weight-bold', '700');

    // Apply fallback common font sizes derived from base (16px)
    const fallbackSizes = this.calculateFontSizes(16);
    rootElement.style.setProperty('--theme-h1-font-size', this.pxToRem(fallbackSizes['h1']));
    this.renderer.setStyle(rootElement, '--theme-h1-font-size', this.pxToRem(fallbackSizes['h1']));
    rootElement.style.setProperty('--theme-h2-font-size', this.pxToRem(fallbackSizes['h2']));
    this.renderer.setStyle(rootElement, '--theme-h2-font-size', this.pxToRem(fallbackSizes['h2']));
    rootElement.style.setProperty('--theme-h3-font-size', this.pxToRem(fallbackSizes['h3']));
    this.renderer.setStyle(rootElement, '--theme-h3-font-size', this.pxToRem(fallbackSizes['h3']));
    rootElement.style.setProperty('--theme-h4-font-size', this.pxToRem(fallbackSizes['h4']));
    this.renderer.setStyle(rootElement, '--theme-h4-font-size', this.pxToRem(fallbackSizes['h4']));
    rootElement.style.setProperty('--theme-h5-font-size', this.pxToRem(fallbackSizes['h5']));
    this.renderer.setStyle(rootElement, '--theme-h5-font-size', this.pxToRem(fallbackSizes['h5']));
    rootElement.style.setProperty('--theme-h6-font-size', this.pxToRem(fallbackSizes['h6']));
    this.renderer.setStyle(rootElement, '--theme-h6-font-size', this.pxToRem(fallbackSizes['h6']));
    rootElement.style.setProperty('--theme-body-font-size', this.pxToRem(fallbackSizes['body']));
    this.renderer.setStyle(rootElement, '--theme-body-font-size', this.pxToRem(fallbackSizes['body']));
    rootElement.style.setProperty('--theme-body-small-font-size', this.pxToRem(fallbackSizes['bodySmall']));
    this.renderer.setStyle(rootElement, '--theme-body-small-font-size', this.pxToRem(fallbackSizes['bodySmall']));
    rootElement.style.setProperty('--theme-caption-font-size', this.pxToRem(fallbackSizes['caption']));
    this.renderer.setStyle(rootElement, '--theme-caption-font-size', this.pxToRem(fallbackSizes['caption']));
    rootElement.style.setProperty('--theme-small-font-size', this.pxToRem(fallbackSizes['small']));
    this.renderer.setStyle(rootElement, '--theme-small-font-size', this.pxToRem(fallbackSizes['small']));

    console.log('✅ TypographyManagementService: Fallback typography applied with "Poppins", sans-serif');
  }

  /**
   * Get scaled font size based on typography scale
   */
  getScaledFontSize(baseSize: number, scaleFactor: number = 1): number {
    return Math.round(baseSize * scaleFactor);
  }

  /**
   * Calculate font sizes for all text styles based on base size and scale
   */
  private pxToRem(px: number): string {
    const rem = px / ROOT_FONT_PX_REFERENCE;
    const rounded = Math.round(rem * 10000) / 10000;
    return `${rounded}rem`;
  }

  calculateFontSizes(baseSize: number, scaleFactor: number = 1): { [key: string]: number } {
    const scale = baseSize * scaleFactor;

    return {
      h1: Math.round(scale * 3),      // 48px at 16px base
      h2: Math.round(scale * 2.5),    // 40px at 16px base
      h3: Math.round(scale * 2),      // 32px at 16px base
      h4: Math.round(scale * 1.5),    // 24px at 16px base
      h5: Math.round(scale * 1.25),   // 20px at 16px base
      h6: Math.round(scale * 1.125),  // 18px at 16px base
      body: Math.round(scale * 1),    // 16px at 16px base
      bodySmall: Math.round(scale * 0.875), // 14px at 16px base
      caption: Math.round(scale * 0.75),    // 12px at 16px base
      small: Math.round(scale * 0.625)       // 10px at 16px base
    };
  }

  /**
   * Get current typography configuration
   */
  getCurrentTypographyConfig(): TypographyConfig | null {
    return this.typographyStore.getCurrentActiveTypographyConfig();
  }

  /**
   * Get current font families
   */
  getCurrentFontFamilies(): FontFamily[] {
    return this.typographyStore.getCurrentFontFamilies();
  }

  /**
   * Get current typography scales
   */
  getCurrentTypographyScales(): TypographyScale[] {
    return this.typographyStore.getCurrentTypographyScales();
  }

  /**
   * Check if typography data is loaded
   */
  isTypographyDataLoaded(): boolean {
    return this.typographyStore.isTypographyDataLoaded();
  }
}
